import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const getAPI = () => (window.__WR_API_BASE__ || '') + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${getAPI()}/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const bodyText = await response.text();
        try {
          const userData = JSON.parse(bodyText);
          setUser(userData);
          setIsGuest(false);
        } catch {
          setUser(null);
        }
      } else {
        // Check localStorage for guest mode
        const guestMode = localStorage.getItem('pcm_guest_mode');
        if (guestMode === 'true') {
          setIsGuest(true);
        }
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const guestId = localStorage.getItem('writeright_guest_id');
    let response;
    try {
      response = await fetch(`${getAPI()}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password, guest_id: guestId })
      });
    } catch (networkErr) {
      throw new Error('Network error — check your connection and try again');
    }

    // Read body as text ONCE, then parse
    let bodyText = '';
    try { bodyText = await response.text(); } catch { bodyText = ''; }

    if (!response.ok) {
      let detail = 'Invalid username or password';
      try {
        const parsed = JSON.parse(bodyText);
        detail = parsed.detail || detail;
      } catch {
        if (response.status === 403) detail = 'Access blocked — your network may be blocking this request.';
        else if (response.status === 401) detail = 'Invalid username or password';
        else detail = `Login failed (${response.status})`;
      }
      throw new Error(detail);
    }

    let userData;
    try {
      userData = JSON.parse(bodyText);
    } catch {
      throw new Error('Login response error — please refresh and try again');
    }
    if (userData.migrated_docs) {
      console.log(`[Migration] ${userData.migrated_docs} guest documents migrated to your account`);
    }
    setUser(userData);
    setIsGuest(false);
    localStorage.removeItem('pcm_guest_mode');
    return userData;
  };

  const register = async (userData) => {
    // Mar 3, 2026 — Attach captured referral code (if any) from the
    // landing-page ?ref=<code> click. `useReferralCapture` stashes it in
    // localStorage on landing; we forward it here so the backend can
    // attribute the signup to the referring provider.
    let referral_code = null;
    try {
      const raw = localStorage.getItem('writeright_referral_code');
      if (raw) {
        const { code, expires } = JSON.parse(raw);
        if (code && (!expires || Date.now() < expires)) referral_code = code;
      }
    } catch { /* ignore */ }
    const payload = referral_code ? { ...userData, referral_code } : userData;

    let response;
    try {
      response = await fetch(`${getAPI()}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
    } catch (networkErr) {
      throw new Error('Network error — check your connection and try again');
    }

    let bodyText = '';
    try { bodyText = await response.text(); } catch { bodyText = ''; }

    if (!response.ok) {
      let detail = 'Registration failed';
      try {
        const parsed = JSON.parse(bodyText);
        detail = parsed.detail || detail;
      } catch {
        if (response.status === 403) detail = 'Access blocked — your network may be blocking this request.';
        else detail = `Registration failed (${response.status})`;
      }
      throw new Error(detail);
    }

    let newUser;
    try {
      newUser = JSON.parse(bodyText);
    } catch {
      throw new Error('Registration response error — please refresh and try again');
    }
    setUser(newUser);
    setIsGuest(false);
    localStorage.removeItem('pcm_guest_mode');
    localStorage.removeItem('writeright_referral_code');
    return newUser;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const guestId = localStorage.getItem('writeright_guest_id');
    // Mar 3, 2026 — forward captured referral code (if any) for signup
    // attribution on the Google-auth path too.
    let referral_code = null;
    try {
      const raw = localStorage.getItem('writeright_referral_code');
      if (raw) {
        const { code, expires } = JSON.parse(raw);
        if (code && (!expires || Date.now() < expires)) referral_code = code;
      }
    } catch { /* ignore */ }

    let response;
    try {
      response = await fetch(`${getAPI()}/auth/google/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId, guest_id: guestId, referral_code })
      });
    } catch (networkErr) {
      throw new Error('Network error during Google sign-in. Try password login instead.');
    }

    let bodyText = '';
    try { bodyText = await response.text(); } catch { bodyText = ''; }

    if (!response.ok) {
      let detail = 'Google authentication failed';
      try {
        const parsed = JSON.parse(bodyText);
        detail = parsed.detail || detail;
      } catch {
        if (response.status === 403) detail = 'Access blocked by network — try password login instead';
        else detail = `Google auth failed (${response.status})`;
      }
      throw new Error(detail);
    }

    let userData;
    try {
      userData = JSON.parse(bodyText);
    } catch {
      throw new Error('Google auth response error — please refresh and try again');
    }
    if (userData.migrated_docs) {
      console.log(`[Migration] ${userData.migrated_docs} guest documents migrated to your account`);
    }
    setUser(userData);
    setIsGuest(false);
    localStorage.removeItem('pcm_guest_mode');
    localStorage.removeItem('writeright_referral_code');
    return userData;
  };

  const logout = async () => {
    try {
      await fetch(`${getAPI()}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsGuest(false);
  };

  const continueAsGuest = () => {
    localStorage.setItem('pcm_guest_mode', 'true');
    setIsGuest(true);
  };

  const updateProfile = async (profileData) => {
    let response;
    try {
      response = await fetch(`${getAPI()}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileData)
      });
    } catch (networkErr) {
      throw new Error('Network error — check your connection and try again');
    }

    let bodyText = '';
    try { bodyText = await response.text(); } catch { bodyText = ''; }

    if (!response.ok) {
      let detail = 'Failed to update profile';
      try {
        const parsed = JSON.parse(bodyText);
        detail = parsed.detail || detail;
      } catch {
        detail = `Failed to update profile (${response.status})`;
      }
      throw new Error(detail);
    }

    let updated;
    try {
      updated = JSON.parse(bodyText);
    } catch {
      throw new Error('Profile update response error — please refresh and try again');
    }
    setUser(prev => ({ ...prev, ...updated }));
    return updated;
  };


  const getSecurityQuestion = async (username) => {
    let response;
    try {
      response = await fetch(`${getAPI()}/auth/security-question?username=${encodeURIComponent(username)}`);
    } catch (networkErr) {
      throw new Error('Network error — check your connection and try again');
    }

    let bodyText = '';
    try { bodyText = await response.text(); } catch { bodyText = ''; }

    if (!response.ok) {
      let detail = 'User not found';
      try {
        const parsed = JSON.parse(bodyText);
        detail = parsed.detail || detail;
      } catch {
        detail = `User not found (${response.status})`;
      }
      throw new Error(detail);
    }

    try {
      return JSON.parse(bodyText);
    } catch {
      throw new Error('Security question response error — please refresh and try again');
    }
  };

  const recoverPassword = async (username, securityAnswer, newPassword) => {
    let response;
    try {
      response = await fetch(`${getAPI()}/auth/recover-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username,
          security_answer: securityAnswer,
          new_password: newPassword
        })
      });
    } catch (networkErr) {
      throw new Error('Network error — check your connection and try again');
    }

    let bodyText = '';
    try { bodyText = await response.text(); } catch { bodyText = ''; }

    if (!response.ok) {
      let detail = 'Password recovery failed';
      try {
        const parsed = JSON.parse(bodyText);
        detail = parsed.detail || detail;
      } catch {
        detail = `Password recovery failed (${response.status})`;
      }
      throw new Error(detail);
    }

    try {
      return JSON.parse(bodyText);
    } catch {
      throw new Error('Password recovery response error — please refresh and try again');
    }
  };

  const value = {
    user,
    loading,
    isGuest,
    isAuthenticated: !!user || isGuest,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    continueAsGuest,
    updateProfile,
    getSecurityQuestion,
    recoverPassword,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
